<template>
  <div v-if="applicant" class="entity-row">
    <!-- Main Row: Ghana Flag + Phone + Link Entity -->
    <div class="entity-main-row">
      <div class="entity-wrapper">
        <!-- Ghana Flag (positioned first, like position card) -->
        <ghana-flag
          :payload="ghanaFlagPayload"
          :eager_load="true"
          class="applicant-ghana-flag"
        />

        <!-- Phone Icon (next to Ghana flag) -->
        <div @click="$emit('applicant-answered')" class="phone-icon-container">
          <img class="operation_icon" width="45" src="/applicant_answered.svg" />
        </div>

        <!-- Link Entity Component -->
        <link-entity
          :payload="applicantPayload"
          :additional_data="{ resource_type: 'Applicant' }"
        />
      </div>
    </div>

    <!-- Icons Row (separate line) -->
    <div class="icons-row">
      <button @click="$emit('open-follow-up')" class="button is-info is-small follow-up-btn">
        <span>Follow up</span>
      </button>
      <button @click="openChatbotPanel" class="button is-link is-small chatbot-btn" title="פתח צ'אטבוט במצב קריאה">
        <span>🤖</span>
      </button>
      <button @click="openPositionRecommendation" class="button is-small recommend-btn" title="המלץ משרות">
        <span>המלץ משרות</span>
      </button>
      <div style="width: 25px;" @click="$emit('open-magic-wand')" class="magic-wand-icon-container">
        <img class="operation_icon" width="25" src="/magic_wand.png" />
      </div>
    </div>

    <!-- Candidate Message (positioned below, like position card) -->
    <div class="candidate-message clickable-message" @click="openMessageForm">
      <span class="message-label">massage:</span>
      <span class="message-text">{{ cleanMessage || 'אין הודעה' }}</span>
    </div>
  </div>
</template>

<script>
import LinkEntity from '@/components/LinkEntity.vue'
import GhanaFlag from '@/components/GhanaFlag.vue'
import { mapMutations } from 'vuex'

export default {
  name: 'ApplicantDetailsBlock',
  components: {
    LinkEntity,
    GhanaFlag
  },
  props: {
    applicant: {
      type: Object,
      required: true
    },
    applicantPayload: {
      type: Object,
      required: true
    },
    ghanaFlagPayload: {
      type: Object,
      required: true
    }
  },
  emits: ['open-follow-up', 'open-magic-wand', 'applicant-answered'],
  computed: {
    cleanMessage () {
      if (!this.applicant.message) return ''

      // Remove XML-style meta tags like <message-meta>, <message-date>, <owner>, etc.
      let cleaned = this.applicant.message
        .replace(/<message-meta>.*?<\/message-meta>/gi, '')
        .replace(/<[^>]+>/g, '') // Remove any remaining HTML/XML tags
        .trim()

      return cleaned
    }
  },
  methods: {
    openMessageForm () {
      if (!this.applicant) return

      this.create({
        designator: 'message-modal',
        component_name: 'MessageForm',
        resource_data: this.applicant,
        additional_data: {
          resource_type: 'Applicant',
          endpoint: 'users',
          headers: 'admin_headers'
        },
        is_submitable: true,
        modal_title: 'Message - ' + (this.applicant.first_name || '') + ' ' + (this.applicant.last_name || '')
      })
    },
    openPositionRecommendation () {
      if (!this.applicant) return
      const applicantId = this.applicant.resource_id || this.applicant.id
      const name = ((this.applicant.first_name || '') + ' ' + (this.applicant.last_name || '')).trim()
      this.create({
        designator: 'position-recommendation-modal',
        component_name: 'PositionRecommendationModal',
        data: { applicant_id: applicantId, applicant_name: name },
        modal_title: 'המלץ על משרות - ' + name,
        modal_width: 1200,
        is_center_modal: true
      })
    },
    openChatbotPanel () {
      if (!this.applicant || !this.applicant.resource_id) return

      const applicantId = this.applicant.resource_id

      // The operation slug tells the readonly panel which tenant API to query.
      const slug = this.$settings.get({ path: 'intake_operation_slug' })
      let chatbotUrl = `https://chatbot.test.panel.hasama.online?applicant_id=${applicantId}&readonly=true`
      if (slug) chatbotUrl += `&operation=${slug}`

      window.open(chatbotUrl, '_blank')
    },
    ...mapMutations('modals', ['create'])
  }
}
</script>

<style scoped>
/* Unified Applicant Block Style - matches Position Block */
.entity-row {
  padding: 0.5rem;
  margin-bottom: 0.8rem;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #48c774;
}

/* Main Row - Ghana Flag + Phone + Link Entity */
.entity-main-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  flex-wrap: nowrap;
}

.entity-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

/* Ensure LinkEntity respects the overflow constraint */
.entity-wrapper :deep(.link-entity),
.entity-wrapper :deep(*) {
  min-width: 0;
  overflow: hidden;
}

/* Let GhanaFlag show all color blocks */
.entity-wrapper :deep(.applicant-ghana-flag),
.entity-wrapper :deep(.applicant-ghana-flag *) {
  overflow: visible;
  min-width: unset;
}

/* Force text truncation for long names */
.entity-wrapper :deep(.entity-link),
.entity-wrapper :deep(a),
.entity-wrapper :deep(span) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Candidate Message - positioned below like position card */
.candidate-message {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 0.5rem;
}

.message-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
  text-align: right;
  align-self: flex-end;
}

.message-text {
  background: #ffffff;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 0.85rem;
  line-height: 1.4;
  word-break: break-word;
  width: 100%;
}

/* Ensure entity content wraps properly */
.entity-row :deep(*) {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Icons Row - separate line below main content */
.icons-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.3rem 0;
  flex-shrink: 0;
}

.phone-icon-container {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.phone-icon-container:hover {
  transform: scale(1.15);
}

/* Phone icon specific styling - higher specificity to override general rule */
.phone-icon-container img.operation_icon {
  max-width: 30px !important;
  min-width: 30px !important;
  width: 30px !important;
  height: auto !important;
}

/* Magic Wand Icon Container */
.magic-wand-icon-container {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.magic-wand-icon-container:hover {
  transform: scale(1.1);
}

.magic-wand-icon-container .operation_icon {
  max-width: 25px !important;
  min-width: 25px !important;
  width: 25px !important;
}

/* Fix icon sizing to match position card - exclude phone icon */
.entity-row :deep(.operation_icon):not(.phone-icon-container .operation_icon),
.entity-row :deep(img.operation_icon):not(.phone-icon-container img.operation_icon) {
  max-width: 18px !important;
  width: 18px !important;
  height: auto !important;
}

/* Clickable Message */
.clickable-message {
  cursor: pointer;
}

.clickable-message:hover {
  background-color: #e8f4f8 !important;
  border-radius: 4px;
}

/* Recommend Button */
.recommend-btn {
  white-space: nowrap;
  background-color: #7c5cbf !important;
  color: white !important;
  border-color: #7c5cbf !important;
  font-size: 0.75rem !important;
}

.recommend-btn:hover {
  background-color: #6a4daa !important;
}

/* Chatbot Button */
.chatbot-btn {
  white-space: nowrap;
  min-width: 40px;
  padding: 0.25rem 0.5rem !important;
  font-size: 1.2rem;
}
</style>
